import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck, Minus, Plus, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";

const PRICE = 200;

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

export const PurchaseForm = () => {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", address: "", city: "", state: "OH", zip_code: "", quantity: 1,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const total = (form.quantity * PRICE).toFixed(2);

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!/^[0-9+\-()\s]{7,}$/.test(form.phone)) e.phone = "Enter a valid phone number";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.state.trim()) e.state = "State is required";
    if (!/^\d{5}(-\d{4})?$/.test(form.zip_code)) e.zip_code = "Enter a valid ZIP code";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please correct the highlighted fields."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        ...form,
        quantity: Number(form.quantity),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  const fieldErr = (k) => errors[k] && <p className="text-xs text-[#D00000] mt-1" data-testid={`error-${k}`}>{errors[k]}</p>;

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="purchase-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="mb-1 block">Full Name</Label>
          <Input data-testid="input-full_name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Abebe Bekele" />
          {fieldErr("full_name")}
        </div>
        <div>
          <Label className="mb-1 block">Phone Number</Label>
          <Input data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 614 555 0100" />
          {fieldErr("phone")}
        </div>
      </div>
      <div>
        <Label className="mb-1 block">Email Address</Label>
        <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
        {fieldErr("email")}
      </div>
      <div>
        <Label className="mb-1 block">Full Address</Label>
        <Textarea data-testid="input-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street address" rows={2} />
        {fieldErr("address")}
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="mb-1 block">City</Label>
          <Input data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Columbus" />
          {fieldErr("city")}
        </div>
        <div>
          <Label className="mb-1 block">State</Label>
          <select data-testid="input-state" value={form.state} onChange={(e) => set("state", e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#9E1B1B]">
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {fieldErr("state")}
        </div>
        <div>
          <Label className="mb-1 block">ZIP Code</Label>
          <Input data-testid="input-zip_code" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="43004" />
          {fieldErr("zip_code")}
        </div>
      </div>

      <div>
        <Label className="mb-1 block">Number of Tickets</Label>
        <div className="flex items-center gap-4">
          <div className="flex items-center border border-[#E2D9C8] rounded-md overflow-hidden">
            <button type="button" data-testid="qty-minus" onClick={() => set("quantity", Math.max(1, form.quantity - 1))}
              className="px-3 py-2 hover:bg-[#F3EFE6] transition-colors"><Minus className="w-4 h-4" /></button>
            <input data-testid="input-quantity" type="number" min="1" max="100" value={form.quantity}
              onChange={(e) => set("quantity", Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              className="w-16 text-center font-mono font-semibold outline-none bg-transparent" />
            <button type="button" data-testid="qty-plus" onClick={() => set("quantity", Math.min(100, form.quantity + 1))}
              className="px-3 py-2 hover:bg-[#F3EFE6] transition-colors"><Plus className="w-4 h-4" /></button>
          </div>
          <span className="text-sm text-[#4A4A4A]">${PRICE.toFixed(2)} per ticket</span>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-lg bg-[#F3EFE6] px-5 py-4">
        <span className="font-medium text-[#4A4A4A]">Total Due</span>
        <span className="font-heading text-3xl font-bold text-[#9E1B1B]" data-testid="order-total">${total}</span>
      </div>

      <Button type="submit" disabled={loading} data-testid="checkout-submit"
        className="w-full bg-[#9E1B1B] hover:bg-[#7A1414] text-white py-6 text-base font-semibold rounded-md transition-colors">
        {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting to secure checkout…</> : <><CreditCard className="w-5 h-5 mr-2" />Pay ${total} Securely</>}
      </Button>

      <div className="flex items-center justify-center gap-6 text-xs text-[#4A4A4A] pt-1">
        <span className="flex items-center gap-1"><Lock className="w-3.5 h-3.5 text-[#2D6A4F]" /> SSL Encrypted</span>
        <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-[#2D6A4F]" /> Powered by Stripe</span>
        <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5 text-[#2D6A4F]" /> PCI Compliant</span>
      </div>
    </form>
  );
};

export default PurchaseForm;
